// src/routes/AdminRoutes.jsx
import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import AdminLoginPage from '../pages/admin/Login/AdminLoginPage.jsx'
import AdminProtectedLayout from '../layouts/AdminProtectedLayout.jsx'
import SkeletonSection from '../components/common/SkeletonSection.jsx'
import { Toaster } from 'react-hot-toast'

// Lazy load admin pages
const AdminDashboardPage = lazy(() => import('../pages/admin/Dashboard/AdminDashboardPage.jsx'))
const CMSPageList = lazy(() => import('../pages/admin/CMS/CMSPageList.jsx'))
const CMSPageDetail = lazy(() => import('../pages/admin/CMS/CMSPageDetail.jsx'))
const ContactSubmissionsPage = lazy(() => import('../pages/admin/Submissions/ContactSubmissionsPage.jsx'))
const EnquirySubmissionsPage = lazy(() => import('../pages/admin/Submissions/EnquirySubmissionsPage.jsx'))
const NewsletterSubmissionsPage = lazy(() => import('../pages/admin/Submissions/NewsletterSubmissionsPage.jsx'))
const CareerSubmissionsPage = lazy(() => import('../pages/admin/Submissions/CareerSubmissionsPage.jsx'))
const ThemeSettingsPage = lazy(() => import('../pages/admin/Settings/ThemeSettingsPage.jsx'))
const EmailSettingsPage = lazy(() => import('../pages/admin/Settings/EmailSettingsPage.jsx'))
const ServicesList = lazy(() => import('../pages/admin/Services/ServiceList.jsx'))
const IndustriesList = lazy(() => import('../pages/admin/Industries/IndustriesList.jsx'))
const BlogsList = lazy(() => import('../pages/admin/Blogs/BlogsList.jsx'))
const CaseStudiesList = lazy(() => import('../pages/admin/CaseStudies/CaseStudiesList.jsx'))
const WebinarsList = lazy(() => import('../pages/admin/Webinars/WebinarsList.jsx'))
const TestimonialsList = lazy(() => import('../pages/admin/Testimonials/TestimonialsList.jsx'))

// Public route wrapper for login - prevents authenticated users from accessing login page
const PublicRoute = ({ children }) => {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')

    // If no token, allow access to login
    if (!token) {
        return children
    }

    // If token exists but invalid user data, clear and allow access
    if (!userStr) {
        localStorage.removeItem('token')
        return children
    }

    try {
        const user = JSON.parse(userStr)
        // If user is admin and token exists, redirect to admin dashboard
        if (user?.role === 'admin') {
            return <Navigate to="/admin/cms" replace />
        }

        // If user exists but not admin, clear storage and allow login
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        return children
    } catch (error) {
        // Invalid JSON in localStorage, clear and allow login
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        return children
    }
}

// Component to handle initial redirects
const AdminRootRedirect = () => {
    const token = localStorage.getItem('token')
    const userStr = localStorage.getItem('user')

    // Check if user might be authenticated
    if (token && userStr) {
        try {
            const user = JSON.parse(userStr)
            if (user?.role === 'admin') {
                // User appears to be admin, let AdminProtectedLayout handle validation
                return <Navigate to="/admin/cms" replace />
            }
        } catch (error) {
            // Invalid data, will be cleared by ProtectedLayout
        }
    }

    // Not authenticated or invalid data, go to login
    return <Navigate to="/admin/login" replace />
}

// Loading fallback component
const AdminLoadingFallback = () => (
    <div className="min-h-screen bg-gray-50 p-8">
        <SkeletonSection />
    </div>
)

// Main AdminRoutes component
export default function AdminRoutes() {
    return (
        <>
            {/* Global Toaster for notifications */}
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '12px 16px',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />

            <Routes>
                {/* Root /admin route - smart redirect based on auth state */}
                <Route path="/" element={<AdminRootRedirect />} />

                {/* Public login route - only accessible when not authenticated */}
                <Route path="/login" element={
                    <PublicRoute>
                        <AdminLoginPage />
                    </PublicRoute>
                } />

                {/* All protected admin routes */}
                <Route element={<AdminProtectedLayout />}>
                    {/* Dashboard */}
                    <Route
                        path="/dashboard"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <AdminDashboardPage />
                            </Suspense>
                        }
                    />

                    {/* CMS Pages */}
                    <Route
                        path="/cms"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <CMSPageList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/cms/pages/:slug"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <CMSPageDetail />
                            </Suspense>
                        }
                    />

                    {/* Content Management */}
                    <Route
                        path="/blogs"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <BlogsList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/case-studies"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <CaseStudiesList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/webinars"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <WebinarsList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/testimonials"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <TestimonialsList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/services"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <ServicesList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/industries"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <IndustriesList />
                            </Suspense>
                        }
                    />

                    {/* Submissions */}
                    <Route
                        path="/submissions/contact"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <ContactSubmissionsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/submissions/enquiry"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <EnquirySubmissionsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/submissions/newsletter"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <NewsletterSubmissionsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/submissions/career"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <CareerSubmissionsPage />
                            </Suspense>
                        }
                    />

                    {/* Settings */}
                    <Route
                        path="/settings/theme"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <ThemeSettingsPage />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/settings/email"
                        element={
                            <Suspense fallback={<AdminLoadingFallback />}>
                                <EmailSettingsPage />
                            </Suspense>
                        }
                    />

                    {/* Catch-all for protected routes - redirect to CMS */}
                    <Route path="*" element={<Navigate to="/admin/cms" replace />} />
                </Route>

                {/* Catch-all route for unmatched paths under /admin */}
                <Route path="*" element={<Navigate to="/admin/login" replace />} />
            </Routes>
        </>
    )
}